import type { ButtonSize } from '@timelyapp/react';
import { Button, PopoverConfirm } from '@timelyapp/react';
import PopoverDropdownList from 'components/popovers/PopoverDropdownList';
import type { InvoiceExportOption, ListOption } from 'factories/invoices';
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { makeGetClientXhrStatusStateByKey } from 'redux/modules/clients.sync';
import { syncInvoice } from 'redux/modules/invoice.actions';
import type { XhrStatusUIState } from 'redux/reusable/xhrStatus';
import type { IState } from 'redux/state';
import type { InvoiceDraft } from 'types/invoice';
import type { ThunkDispatch } from 'types/redux';
import styles from './InvoiceExportButton.module.scss';

interface ExternalProps {
  invoice: InvoiceDraft;
  options: Array<InvoiceExportOption>;
  // Options includes headers, this is the number of actual options
  optionsCount: number;
  buttonSize?: ButtonSize;
}

interface StateProps {
  xhrStatus: XhrStatusUIState;
}

type Props = ExternalProps & StateProps;

const makeMapStateToProps = () => {
  const getXhrStatusStateByKey = makeGetClientXhrStatusStateByKey();
  const mapStateToProps = (state: IState, ownProps: ExternalProps) => {
    const key = ownProps.invoice?.project?.client?.id.toString() || '';

    return { xhrStatus: getXhrStatusStateByKey(state, { key }) };
  };
  return mapStateToProps;
};

const InvoiceExportButton: React.FC<Props> = ({
  invoice,
  options,
  optionsCount,
  xhrStatus,
  buttonSize,
}) => {
  const { isLoading, isSaving } = xhrStatus;
  const disabledOrLoading = isLoading || isSaving || invoice.isAwaitingSync;
  const dispatch = useDispatch<ThunkDispatch>();

  if (invoice.deleted) {
    return <span>—</span>;
  }

  function onClick() {
    dispatch(syncInvoice(invoice));
  }

  if (optionsCount === 1) {
    const exportOption = options[1] as ListOption;

    if (!exportOption) {
      return null;
    }

    const button = (
      <Button
        onClick={() => {
          if (!exportOption.confirm) {
            dispatch(syncInvoice(invoice));
          }
        }}
        disabled={disabledOrLoading}
        isLoading={disabledOrLoading}
        size={buttonSize}
        caret={false}
      >
        <span>Send to {exportOption.provider}</span>
      </Button>
    );

    if (exportOption.confirm) {
      return (
        <PopoverConfirm
          className={styles.confirmWrap}
          onConfirm={onClick}
          target={button}
          prompt={exportOption.confirmMessage}
          confirmButtonPriority="high"
        />
      );
    }

    return button;
  }

  return (
    <PopoverDropdownList
      isOpen={disabledOrLoading ? false : undefined}
      confirmButtonPriority="high"
      target={
        <Button
          disabled={disabledOrLoading || !options.length}
          isLoading={disabledOrLoading}
          size={buttonSize}
          caret={false}
        >
          Send to...
        </Button>
      }
      fixed
      options={options}
      onClick={onClick}
    />
  );
};

export default connect<StateProps, {}, ExternalProps, IState>(makeMapStateToProps)(
  InvoiceExportButton
);
